import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  exportQuestions,
  useAnswerQuestion,
  useAskQuestion,
  useAssignQuestion,
  useDocuments,
  useFolders,
  useGroups,
  useMembers,
  usePublishQuestion,
  useQuestion,
  useQuestions,
  useSetQuestionStatus,
} from '../api/rooms'
import type { QuestionResponse, QuestionStatus, RoomResponse } from '../api/types'
import { askTargets } from '../lib/askTargets'
import { useAuth } from '../auth/AuthContext'
import { errorMessage, formatUtc } from '../lib/format'

/**
 * Q&A of the room. The list is what the server says the caller may see; every control
 * on a question is drawn from the server's `canAnswer` on that question, never from the
 * caller's role alone.
 */
export function Questions() {
  const room = useOutletContext<RoomResponse>()
  const { roomId = '' } = useParams()
  const [filter, setFilter] = useState<QuestionStatus | ''>('')
  const [selected, setSelected] = useState<string | null>(null)
  const [asking, setAsking] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const questions = useQuestions(roomId, filter || undefined)
  const folders = useFolders(roomId)
  const { me } = useAuth()
  const targets = askTargets(room, folders.data)
  const canAsk = targets.canAsk
  const canExport =
    room.memberRole === 'Owner' ||
    (room.permissions.viewAudit &&
      ['auditor', 'room-owner', 'platform-admin'].some((r) => me?.roles.includes(r)))

  return (
    <div className="stack">
      <section className="panel">
        <div className="row-between">
          <h2 className="h2 no-margin">Questions</h2>
          <div className="row-actions">
            <label className="small muted">
              Show{' '}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as QuestionStatus | '')}
                aria-label="Filter questions by status"
              >
                <option value="">all</option>
                <option value="Open">open</option>
                <option value="Answered">answered</option>
                <option value="Closed">closed</option>
              </select>
            </label>
            {canExport && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setExportError(null)
                  exportQuestions(roomId).catch((e: unknown) => setExportError(errorMessage(e)))
                }}
              >
                Export CSV
              </button>
            )}
            {canAsk && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setAsking(true)}
              >
                Ask a question
              </button>
            )}
          </div>
        </div>
        {exportError && <p className="error-text small">{exportError}</p>}
        <p className="small muted">
          A question is seen by you, your group, and the room&apos;s owners until an owner publishes
          the answer to everyone or to other groups.
        </p>
        {asking && (
          <AskForm
            roomId={roomId}
            targets={targets}
            onDone={(id) => {
              setAsking(false)
              if (id) setSelected(id)
            }}
          />
        )}
        {questions.isPending && <p className="muted small">Loading questions…</p>}
        {questions.isError && <p className="error-text small">{errorMessage(questions.error)}</p>}
        {questions.data && questions.data.length === 0 && (
          <p className="muted small">
            No questions{filter ? ` (${filter.toLowerCase()})` : ''} yet.
          </p>
        )}
        {questions.data && questions.data.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Subject</th>
                <th>About</th>
                <th>Asked by</th>
                <th>Status</th>
                <th>Answers</th>
                <th>Visible to</th>
              </tr>
            </thead>
            <tbody>
              {questions.data.map((q) => (
                <tr
                  key={q.id}
                  className={q.id === selected ? 'row-clickable row-selected' : 'row-clickable'}
                  onClick={() => setSelected(q.id)}
                >
                  <td className="mono small">Q-{q.number}</td>
                  <td>
                    <button type="button" className="link-btn" onClick={() => setSelected(q.id)}>
                      {q.subject}
                    </button>
                  </td>
                  <td className="small">
                    <TargetLabel q={q} />
                  </td>
                  <td className="small">
                    {q.askedByName ?? q.askedByEmail ?? '—'}
                    <div className="muted">{formatUtc(q.askedUtc)}</div>
                  </td>
                  <td>
                    <StatusChip status={q.status} />
                  </td>
                  <td className="small">{q.answerCount}</td>
                  <td className="small muted">
                    <Audience q={q} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {selected && (
        <QuestionDetail
          roomId={roomId}
          room={room}
          questionId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

function TargetLabel({ q }: { q: QuestionResponse }) {
  if (q.targetType === 'Room') return <span>the room</span>
  return (
    <span>
      {q.targetType === 'Folder' ? 'folder ' : 'document '}
      {q.targetIndex && <span className="mono">{q.targetIndex} </span>}
      {q.targetName}
      {q.targetDeleted && <span className="chip chip-warn"> deleted</span>}
    </span>
  )
}

export function Audience({ q }: { q: QuestionResponse }) {
  if (q.publishedToAll) return <span>everyone</span>
  const parts: string[] = []
  if (q.submitterGroups) parts.push(q.submitterGroups)
  if (q.publishedGroups) parts.push(`published to ${q.publishedGroups}`)
  return <span>{parts.length > 0 ? parts.join('; ') : 'asker and owners'}</span>
}

export function StatusChip({ status }: { status: QuestionStatus }) {
  const cls = status === 'Open' ? 'chip chip-warn' : status === 'Answered' ? 'chip' : 'chip muted'
  return <span className={cls}>{status}</span>
}

const askSchema = z.object({
  targetType: z.enum(['Room', 'Folder', 'Document']),
  targetId: z.string(),
  subject: z.string().trim().min(1, 'Give the question a subject').max(200),
  body: z.string().trim().min(1, 'Write the question').max(20000),
})
type AskForm = z.infer<typeof askSchema>

function AskForm({
  roomId,
  targets,
  onDone,
}: {
  roomId: string
  targets: ReturnType<typeof askTargets>
  onDone: (id: string | null) => void
}) {
  const ask = useAskQuestion(roomId)
  const form = useForm<AskForm>({
    resolver: zodResolver(askSchema),
    defaultValues: { targetType: targets.types[0] ?? 'Room', targetId: '', subject: '', body: '' },
  })
  const targetType = form.watch('targetType')
  const folderId = form.watch('targetId')
  // Documents are listed per folder; for a document question the folder is chosen first.
  const [docFolder, setDocFolder] = useState<string | null>(
    targets.roomWide ? null : (targets.folders[0]?.id ?? null),
  )
  const documents = useDocuments(roomId, docFolder)

  return (
    <form
      className="form"
      onSubmit={form.handleSubmit(async (values) => {
        try {
          const q = await ask.mutateAsync({
            targetType: values.targetType,
            targetId: values.targetType === 'Room' ? null : values.targetId || null,
            subject: values.subject,
            body: values.body,
          })
          form.reset()
          onDone(q.id)
        } catch {
          /* shown below */
        }
      })}
    >
      <div className="field">
        <label>
          About
          <select
            value={targetType}
            onChange={(e) => {
              form.setValue('targetType', e.target.value as AskForm['targetType'])
              form.setValue('targetId', '')
            }}
          >
            {targets.types.includes('Room') && <option value="Room">the room in general</option>}
            {targets.types.includes('Folder') && <option value="Folder">a folder</option>}
            {targets.types.includes('Document') && <option value="Document">a document</option>}
          </select>
        </label>
      </div>
      {targetType === 'Folder' && (
        <div className="field">
          <label>
            Folder
            <select {...form.register('targetId')} required>
              <option value="">Choose a folder</option>
              {targets.folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.indexNumber} {f.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {targetType === 'Document' && (
        <>
          <div className="field">
            <label>
              Folder
              <select
                value={docFolder ?? ''}
                onChange={(e) => {
                  setDocFolder(e.target.value || null)
                  form.setValue('targetId', '')
                }}
              >
                {targets.roomWide && <option value="">Room root</option>}
                {targets.folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.indexNumber} {f.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="field">
            <label>
              Document
              <select {...form.register('targetId')} required>
                <option value="">Choose a document</option>
                {documents.data
                  ?.filter((d) => d.permissions.askQuestion)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        </>
      )}
      <div className="field">
        <label>
          Subject
          <input type="text" maxLength={200} {...form.register('subject')} />
        </label>
        {form.formState.errors.subject && (
          <p className="field-error">{form.formState.errors.subject.message}</p>
        )}
      </div>
      <div className="field">
        <label>
          Question
          <textarea rows={4} {...form.register('body')} />
        </label>
        {form.formState.errors.body && (
          <p className="field-error">{form.formState.errors.body.message}</p>
        )}
      </div>
      {ask.isError && <p className="error-text small">{errorMessage(ask.error)}</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={ask.isPending || (targetType !== 'Room' && !folderId)}
        >
          {ask.isPending ? 'Sending…' : 'Ask'}
        </button>
        <button type="button" className="btn" onClick={() => onDone(null)}>
          Cancel
        </button>
      </div>
    </form>
  )
}

function QuestionDetail({
  roomId,
  room,
  questionId,
  onClose,
}: {
  roomId: string
  room: RoomResponse
  questionId: string
  onClose: () => void
}) {
  const question = useQuestion(roomId, questionId)
  const answer = useAnswerQuestion(roomId)
  const setStatus = useSetQuestionStatus(roomId)
  const [reply, setReply] = useState('')
  const { me } = useAuth()

  if (question.isPending) return <p className="muted small">Loading question…</p>
  if (question.isError) return <p className="error-text small">{errorMessage(question.error)}</p>
  const q = question.data
  const roleAllowsAnswer = ['contributor', 'room-owner', 'platform-admin'].some((r) =>
    me?.roles.includes(r),
  )
  const canAct = q.canAnswer && roleAllowsAnswer && room.status === 'Open'

  return (
    <section className="panel" aria-label={`Question ${q.number}`}>
      <div className="row-between">
        <h2 className="h2 no-margin">
          <span className="mono small">Q-{q.number}</span> {q.subject}
        </h2>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="small muted">
        Asked by {q.askedByName ?? q.askedByEmail ?? 'a participant'} on {formatUtc(q.askedUtc)}{' '}
        about <TargetLabel q={q} />. <StatusChip status={q.status} />{' '}
        {q.assignedToName && <span>· assigned to {q.assignedToName}</span>}
      </p>
      <p className="nda-body">{q.body}</p>

      <h3 className="h3">Answers</h3>
      {(q.answers ?? []).length === 0 && <p className="muted small">No answer yet.</p>}
      {(q.answers ?? []).map((a) => (
        <div key={a.id} className="card">
          <p className="nda-body no-margin">{a.body}</p>
          <p className="small muted">
            {a.answeredByName ?? a.answeredByEmail ?? 'an owner'} · {formatUtc(a.answeredUtc)}
          </p>
        </div>
      ))}

      {canAct && (
        <>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault()
              if (!reply.trim()) return
              answer.mutate(
                { questionId: q.id, body: reply.trim() },
                { onSuccess: () => setReply('') },
              )
            }}
          >
            <div className="field">
              <label>
                Your answer
                <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} />
              </label>
            </div>
            {answer.isError && <p className="error-text small">{errorMessage(answer.error)}</p>}
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={answer.isPending || !reply.trim()}
              >
                {answer.isPending ? 'Sending…' : 'Answer'}
              </button>
              {q.status !== 'Closed' ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setStatus.mutate({ questionId: q.id, status: 'Closed' })}
                >
                  Close question
                </button>
              ) : (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setStatus.mutate({ questionId: q.id, status: 'Open' })}
                >
                  Reopen
                </button>
              )}
            </div>
            {setStatus.isError && (
              <p className="error-text small">{errorMessage(setStatus.error)}</p>
            )}
          </form>
          <OwnerControls roomId={roomId} q={q} />
        </>
      )}
    </section>
  )
}

function OwnerControls({ roomId, q }: { roomId: string; q: QuestionResponse }) {
  const members = useMembers(roomId)
  const groups = useGroups(roomId)
  const assign = useAssignQuestion(roomId)
  const publish = usePublishQuestion(roomId)
  const [groupIds, setGroupIds] = useState<string[]>([])

  return (
    <div className="stack-sm">
      <div className="inline-form">
        <label className="small">
          Assigned to{' '}
          <select
            value={q.assignedToId ?? ''}
            onChange={(e) =>
              assign.mutate({ questionId: q.id, assigneeId: e.target.value || null })
            }
            disabled={assign.isPending}
          >
            <option value="">nobody</option>
            {members.data?.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName ?? m.email ?? m.userId}
              </option>
            ))}
          </select>
        </label>
        {assign.isError && <span className="error-text small">{errorMessage(assign.error)}</span>}
      </div>
      <div className="inline-form">
        <span className="small">
          Visible to: <Audience q={q} />
        </span>
        {!q.publishedToAll && (
          <>
            <button
              type="button"
              className="btn btn-sm"
              disabled={publish.isPending}
              onClick={() => publish.mutate({ questionId: q.id, toAll: true, groupIds: [] })}
            >
              Publish to everyone
            </button>
            {groups.data && groups.data.length > 0 && (
              <>
                <select
                  multiple
                  aria-label="Groups to publish to"
                  value={groupIds}
                  onChange={(e) =>
                    setGroupIds(Array.from(e.target.selectedOptions).map((o) => o.value))
                  }
                >
                  {groups.data.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={publish.isPending || groupIds.length === 0}
                  onClick={() =>
                    publish.mutate(
                      { questionId: q.id, toAll: false, groupIds },
                      { onSuccess: () => setGroupIds([]) },
                    )
                  }
                >
                  Publish to selected groups
                </button>
              </>
            )}
          </>
        )}
        {publish.isError && <span className="error-text small">{errorMessage(publish.error)}</span>}
      </div>
      <p className="small muted">
        Publishing cannot be undone: once shown to a group, an answer stays visible to it.
      </p>
    </div>
  )
}
